/**
 * The variables a run may not set, because they route around the assumed role.
 *
 * A run's `variables` document is forwarded to the installer verbatim: the API
 * casts the body, the dispatch worker merges the secrets back, and the runner
 * wrapper writes the whole thing into the container's `config.json` as
 * `{account_config: {...}, variables: .}`. Nothing in that path refuses a name.
 *
 * Four names in the installer's own `variables.tf` are therefore reachable from
 * a request body, and they are the one thing this product's design does not
 * allow. The installer's AWS credentials come from an `sts:AssumeRole` performed
 * **inside the container, under the runner's own identity** — that is what the
 * time-bounded grant condition gates, and it is what puts `qim-<email>` in the
 * customer's own CloudTrail. `initialize.sh` takes credentials from
 * `account_config` *or* from ordinary variables, so a caller who supplies them
 * runs Terraform in someone else's account with credentials this side chose,
 * outside the grant window and attributed to nobody.
 *
 * Not exploitable from the console today, and that is the whole of the
 * protection: the run form has eleven boxes and none is named any of these. It
 * is reachable with `curl` by anyone holding `deployment:create`, and reachable
 * in one click as soon as the console can edit the variables document — which is
 * why this lands before the editor rather than beside it.
 *
 * ## What this deliberately is not
 *
 * **Not `isSecretVariableName`.** That decides what may be *remembered*, and
 * over-matches on the shape of a name so a future secret under an unheard-of
 * name is still kept out of an execution history — it catches `accountKey` too,
 * harmlessly. This decides what may be *sent*, where over-matching refuses runs
 * that would have worked. The two rules point in opposite directions, so neither
 * can be derived from the other and both exist.
 *
 * **Not an allow list.** Which variables are legal is a property of the chart
 * version, and IM-62 is where that comes from. Refusing four named credentials
 * is the part that can be justified without the schema; waiting for the schema
 * to refuse them is waiting for the wrong thing.
 *
 * **Not recursive.** The document's top level *is* the installer's Terraform
 * variable namespace, so that is where these names bind. A nested
 * `customer_info.aws_session_token` is an ordinary string in a block the
 * installer reads for a company name, and refusing it would be the over-match
 * this rule cannot afford. `account_config` is not reachable at all: the wrapper
 * builds it and nests the caller's document *underneath* it.
 */
/**
 * The names a run may not carry, as the installer declares them.
 *
 * The first three are AWS credentials; `deployer_principal_arn_override` is the
 * same class by another route — it names a principal to act as rather than
 * supplying one's keys, and the answer to "which identity does this run use" is
 * not a caller's to give.
 */
export const REFUSED_VARIABLE_NAMES = [
    'aws_access_key_id',
    'aws_secret_access_key',
    'aws_session_token',
    'deployer_principal_arn_override',
];
/**
 * Case and surrounding whitespace are stripped before matching.
 *
 * Neither variant would actually bind — Terraform variable names are
 * case-sensitive and a JSON key of `" aws_session_token "` names no variable —
 * so this is not closing a hole so much as refusing to be the thing that decides
 * it is not one. A request carrying `AWS_SECRET_ACCESS_KEY` is not a request to
 * pass through on a technicality about the installer's parser.
 */
const normalize = (name) => name.trim().toLowerCase();
const REFUSED = new Set(REFUSED_VARIABLE_NAMES);
/**
 * What is wrong with a run's variables, if anything — before a run is recorded.
 *
 * Shared so the console refuses before the request and the API refuses before
 * the record is written, with one wording from one function — the arrangement
 * `deploymentIdProblem` and `validateRegistration` already use.
 *
 * The message says *why*, because "not allowed" invites the reader to look for
 * the flag that allows it. There is no such flag: supplying credentials is not a
 * permission this product withholds, it is a thing the product is built not to
 * do.
 */
export const refusedVariableProblem = (variables) => {
    for (const name of Object.keys(variables)) {
        if (!REFUSED.has(normalize(name)))
            continue;
        return `${normalize(name)} cannot be set on a run. Which identity the installer acts as is decided by the sts:AssumeRole it performs inside the container, under the runner's own identity — that is what the time-bounded grant permits, and what attributes the work to a person in the customer's own CloudTrail. A variable supplying credentials or naming a different principal would route around both.`;
    }
    return undefined;
};
//# sourceMappingURL=refused.js.map